'use client';
import React, { useEffect, useRef } from 'react';

const BOOKING_URL = 'https://revmy.ro/service-provider-profile/botosani/detailing/asa-detailing';

const priceGroups = [
  {
    id: 'retapitare',
    title: 'Retapitare Plafoane',
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" strokeLinecap="round" strokeLinejoin="round"/>
        <polyline points="9 22 9 12 15 12 15 22" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    items: [
      { label: 'Plafon', price: '600 lei' },
      { label: 'Stâlpi', price: '90 lei / bucată' },
      { label: 'Portiere', price: '130 lei / bucată' },
    ],
  },
  {
    id: 'interior',
    title: 'Curățare Interior',
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M8 12h8M12 8v8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    items: [
      { label: 'Interior Rapid', price: '150 lei' },
      { label: 'Interior Basic', price: '400 lei' },
      { label: 'Interior Premium', price: '650 lei' },
    ],
  },
  {
    id: 'polish',
    title: 'Polish & Protecție',
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    items: [
      { label: 'Polish One Step', price: '700 lei' },
      { label: 'Polish în mai mulți pași', price: '1.000 lei' },
      { label: 'Ceară solidă caroserie', price: '500 lei' },
    ],
  },
  {
    id: 'extras',
    title: 'Servicii Extra',
    icon: (
      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    items: [
      { label: 'Chrome Delete', price: '600 lei' },
      { label: 'Lumini ambientale', price: '900 lei' },
      { label: 'Plafon înstelat', price: '3 lei / stea' },
    ],
  },
];

export default function ServiceTiersSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) obs?.observe(sectionRef?.current);
    return () => obs?.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-24 md:py-32" style={{ background: 'var(--cockpit-black)' }}>
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="metric-pill mb-5 reveal">Prețuri</div>
          <h2
            className="font-manrope font-extrabold text-white reveal reveal-delay-1"
            style={{ fontSize: 'clamp(28px, 4.5vw, 52px)', letterSpacing: '-0.03em' }}
          >
            Prețurile încep de la
          </h2>
          <p className="mt-4 max-w-lg mx-auto reveal reveal-delay-2" style={{ color: 'var(--aluminum-dim)', fontSize: '16px' }}>
            Servicii profesionale de detailing auto. Prețul final poate varia în funcție de starea și dimensiunea vehiculului.
          </p>
        </div>

        {/* Price groups grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {priceGroups?.map((group, gi) => (
            <div
              key={group?.id}
              className={`reveal reveal-delay-${gi + 1} rounded-2xl p-6`}
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(63,63,70,0.6)',
              }}
            >
              {/* Group header */}
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', color: 'var(--ceramic-blue)' }}
                >
                  {group?.icon}
                </div>
                <h3 className="font-manrope font-bold text-white" style={{ fontSize: '16px', letterSpacing: '-0.01em' }}>
                  {group?.title}
                </h3>
              </div>

              {/* Items */}
              <ul className="space-y-3">
                {group?.items?.map((item) => (
                  <li key={item?.label} className="flex items-center justify-between gap-4">
                    <span style={{ fontSize: '14px', color: 'rgba(212,212,216,0.85)', fontFamily: 'DM Sans, sans-serif' }}>
                      {item?.label}
                    </span>
                    <span
                      className="font-manrope font-bold shrink-0"
                      style={{ fontSize: '15px', color: 'var(--ceramic-blue)', letterSpacing: '-0.01em' }}
                    >
                      {item?.price}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* PPF & Colantare — contact only */}
        <div
          className="reveal reveal-delay-4 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-5"
          style={{
            background: 'rgba(59,130,246,0.06)',
            border: '1px solid rgba(59,130,246,0.25)',
          }}
        >
          <div className="flex items-center gap-4">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)' }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: 'var(--ceramic-blue)' }}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <div className="font-manrope font-bold text-white" style={{ fontSize: '15px' }}>
                PPF & Colantare
              </div>
              <div style={{ fontSize: '14px', color: 'var(--aluminum-dim)' }}>
                Prețul variază în funcție de proiect — contactează-ne pentru o ofertă personalizată.
              </div>
            </div>
          </div>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary shrink-0"
            style={{ whiteSpace: 'nowrap' }}
          >
            Contactează-ne
          </a>
        </div>
      </div>
    </section>
  );
}